import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useSwitchWorkspace } from '../../../workspaces/useSwitchWorkspace';
import { WorkspaceSwitcherSection } from '../../../workspaces/WorkspaceSwitcherSection';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { AccountMenuHeader } from '../account/AccountMenuHeader';
import { AccountMenuList } from '../account/AccountMenuList';
import { accountMenuMessages as messages } from '../account/accountMenuMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole sidebar; each part uses its own slice of it */
import styles from '../../SMLSideBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SidebarAccountMenuProps {
  id: string;
  email: string;
  accountAttention: number;
  /** The card button: a click on it is not a click outside. */
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  onOpenMobileApp: () => void;
}

/**
 * design: navigation-v2 6a. The account menu opens above the account card (on the rail: to the
 * right of it). It holds the keyboard: focus moves in, Tab wraps, Escape and a click outside close
 * it and give the focus back to the card. With 2 or more workspaces, the workspace switch comes
 * under the header (it was in the sidebar artist switcher, design workspaces a1).
 */
const SidebarAccountMenuComponent: React.FC<SidebarAccountMenuProps> = ({
  id,
  email,
  accountAttention,
  anchorRef,
  onClose,
  onOpenMobileApp,
}) => {
  const intl = useIntl();
  const menuRef = useRef<HTMLDivElement>(null);
  const workspaceSwitch = useSwitchWorkspace(onClose);
  // Escape gives the focus back to the card, also when the click that opened the menu did not focus it.
  const closeWithKey = useCallback(() => {
    onClose();
    anchorRef.current?.focus();
  }, [anchorRef, onClose]);
  useFocusTrap(menuRef, true, closeWithKey);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent | TouchEvent): void => {
      const { target } = event;
      if (!(target instanceof Node)) {
        return;
      }
      if (menuRef.current?.contains(target) === true || anchorRef.current?.contains(target) === true) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [anchorRef, onClose]);

  return (
    <div
      id={id}
      ref={menuRef}
      className={styles.accountMenu}
      role="dialog"
      aria-label={intl.formatMessage(messages.menu)}
    >
      <AccountMenuHeader email={email} />
      <WorkspaceSwitcherSection variant="menu" switcher={workspaceSwitch} onClose={onClose} />
      <AccountMenuList accountAttention={accountAttention} onNavigate={onClose} onOpenMobileApp={onOpenMobileApp} />
    </div>
  );
};

export const SidebarAccountMenu = TypedMemo(SidebarAccountMenuComponent);
